import React, { useCallback, useContext, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { ScreenSize, UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ENUM_TYPE } from "src/stores/EnumerationStore";
import { NEWS_LIST_TAB, NEWS_LIST_TAB_STATES } from "src/stores/NewsListStore";
import * as Screens from "src/screens";
import { NewsState } from "src/api/news";
import { getDateWithLongMonth, getDateWithLongMonthOfUtcTimestamp } from "src/utils/Date";
import { getNewsEditRoute, getNewsPreviewRoute } from "src/utils/NewsRoutes";
import { getDisplayedRealestates, INewsListItem } from "src/utils/NewsList";
import { useNewsListFilter } from "src/screens/news/useNewsListFilter";
import styles from "./NewsOverviewList.module.css";

// Date, title, realestates, author, last edited, status
const COLUMN_WIDTHS = [0.6, 1, 1, 0.9, 0.6, 0.5];
const NEWS_PER_PAGE = 20;
const EMPTY_VALUE = "-";
const NEWS_STATE_CLASS_NAMES = new Map<number, string>([
    [NewsState.Editing, styles.StatusEditing],
    [NewsState.Published, styles.StatusPublished]
]);

const NewsOverviewRealestates = ({ newsItem }: { newsItem: INewsListItem }) => {
    const { t } = useTranslation();
    const { displayedRealestates, moreRealestatesCount } = getDisplayedRealestates(newsItem);

    return (
        <div className={styles.CellLines}>
            {displayedRealestates.map((realestate) => (
                <div key={realestate.id} className={styles.CellLine}>
                    {realestate.name}
                </div>
            ))}
            {moreRealestatesCount > 0 && (
                <div>{t("screens.news.list.more_realestates", { count: moreRealestatesCount })}</div>
            )}
        </div>
    );
};

// Name and e-mail, the e-mail only if it is not already the name. Authors that are no user, e.g. the ERP, are unknown.
const NewsOverviewAuthor = ({ newsItem }: { newsItem: INewsListItem }) => {
    const { author } = newsItem;

    return (
        <div className={styles.CellLines}>
            <div className={styles.CellLine}>{author?.name ?? EMPTY_VALUE}</div>
            {author?.email && author.email !== author.name && <div className={styles.CellLine}>{author.email}</div>}
        </div>
    );
};

const NewsOverviewTabsBase = () => {
    const { t } = useTranslation();
    const { uiStore, enumerationsStore, newsListStore } = useContext(RootStoreContext);

    const newsStatusMap = enumerationsStore.getEnumMapByType(ENUM_TYPE.NEWS_STATES);
    const tabs = useMemo(
        () =>
            Object.values(NEWS_LIST_TAB).map((tab) => {
                const tabState = NEWS_LIST_TAB_STATES[tab];
                return {
                    id: tab,
                    label: tabState === undefined ? t("labels.all") : (newsStatusMap.get(tabState)?.label ?? "")
                };
            }),
        [newsStatusMap, t]
    );

    const { setFilter } = useNewsListFilter();
    const onTabChange = useCallback((tab: string) => setFilter({ tab: tab as NEWS_LIST_TAB }), [setFilter]);

    if (uiStore.isMobile) {
        return (
            <UI.Dropdown
                id="news-tabs"
                onSelect={onTabChange}
                items={tabs.map((tab) => ({ label: tab.label, value: tab.id }))}
                label={tabs.find((tab) => tab.id === newsListStore.filter.tab)?.label ?? ""}
                className="mb-2"
            />
        );
    }

    return (
        <div className={styles.Tabbar}>
            <UI.Tabs selectedId={newsListStore.filter.tab} onTabChange={onTabChange} contents={tabs} nowrap />
        </div>
    );
};
const NewsOverviewTabs = observer(NewsOverviewTabsBase);

const NewsOverviewListBase = () => {
    const { t } = useTranslation();
    const { uiStore, authStore, enumerationsStore, newsListStore } = useContext(RootStoreContext);

    // The page is kept in the store only. A changed filter starts the list again, on the page the store sets (1).
    const listKey = JSON.stringify(newsListStore.filter);

    const newsStatusMap = enumerationsStore.getEnumMapByType(ENUM_TYPE.NEWS_STATES);

    const renderItem = useCallback(
        (newsItem: INewsListItem, screenSize: ScreenSize) => {
            // A published news opens in the preview, any other news in the mask.
            const link =
                newsItem.state === NewsState.Published
                    ? getNewsPreviewRoute(newsItem.id, undefined, authStore.user?.role)
                    : getNewsEditRoute(newsItem.id, undefined, authStore.user?.role);
            const date = getDateWithLongMonth(newsItem.timestamp ?? "") ?? EMPTY_VALUE;
            const status = (
                <span className={`${styles.Status} ${NEWS_STATE_CLASS_NAMES.get(newsItem.state) ?? ""}`}>
                    {newsStatusMap.get(newsItem.state)?.label}
                </span>
            );

            const listRows =
                screenSize === ScreenSize.MOBILE
                    ? [
                          <UI.List.Row key={`row-mobile-${newsItem.id}`} className={styles.MobileRow}>
                              <div className={styles.MobileTop}>
                                  <strong className={styles.MobileTitle}>{newsItem.title}</strong>
                                  {status}
                              </div>
                              <div className={styles.MobileBottom}>
                                  <NewsOverviewRealestates newsItem={newsItem} />
                                  <span>{date}</span>
                              </div>
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key={`row-${newsItem.id}`} className={styles.Row}>
                              <UI.List.Cell key="date" colspan={COLUMN_WIDTHS[0]} value={date} truncate />
                              <UI.List.Cell key="title" colspan={COLUMN_WIDTHS[1]} value={newsItem.title} truncate />
                              <UI.List.Cell
                                  key="realestates"
                                  colspan={COLUMN_WIDTHS[2]}
                                  value={<NewsOverviewRealestates newsItem={newsItem} />}
                                  truncate
                              />
                              <UI.List.Cell
                                  key="author"
                                  colspan={COLUMN_WIDTHS[3]}
                                  value={<NewsOverviewAuthor newsItem={newsItem} />}
                                  truncate
                              />
                              <UI.List.Cell
                                  key="updated"
                                  colspan={COLUMN_WIDTHS[4]}
                                  value={getDateWithLongMonthOfUtcTimestamp(newsItem.updated)}
                                  truncate
                              />
                              <UI.List.Cell key="status" colspan={COLUMN_WIDTHS[5]} value={status} truncate />
                          </UI.List.Row>
                      ];

            return (
                <UI.List.Item
                    key={`item-${newsItem.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                    to={link}
                    isClickableItemWithoutChevron
                />
            );
        },
        [authStore.user?.role, newsStatusMap]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const headerCells = [
                <UI.List.Cell key="date" colspan={COLUMN_WIDTHS[0]} value={t("screens.news.list.date")} truncate />,
                <UI.List.Cell key="title" colspan={COLUMN_WIDTHS[1]} value={t("screens.news.list.title")} truncate />,
                <UI.List.Cell
                    key="realestates"
                    colspan={COLUMN_WIDTHS[2]}
                    value={t("screens.news.list.realestates")}
                    truncate
                />,
                <UI.List.Cell key="author" colspan={COLUMN_WIDTHS[3]} value={t("screens.news.list.author")} truncate />,
                <UI.List.Cell
                    key="updated"
                    colspan={COLUMN_WIDTHS[4]}
                    value={t("screens.news.list.updated")}
                    truncate
                />,
                <UI.List.Cell key="status" colspan={COLUMN_WIDTHS[5]} value={t("screens.news.list.status")} truncate />
            ];

            return <UI.List.Header rows={headerCells} className="p-0" />;
        },
        [t]
    );

    const renderList = () => {
        if (newsListStore.isLoading) {
            return <Screens.Loading />;
        }
        if (newsListStore.hasLoadingError) {
            return <Screens.Error message={t("screens.news.list.loading_error")} />;
        }
        return (
            <UI.List.BasicList
                key={listKey}
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={newsListStore.displayedNewsItems}
                renderItem={renderItem}
                renderHeader={renderHeader}
                itemsPerPage={NEWS_PER_PAGE}
                // Back from a news, the list opens on the page the user left.
                initialPage={newsListStore.currentPage}
                onPageChanged={newsListStore.setCurrentPage}
                emptyListMessage={t("screens.news.list.no_news").toString()}
            />
        );
    };

    return (
        <UI.Card flex={true}>
            <NewsOverviewTabs />
            {renderList()}
        </UI.Card>
    );
};

export const NewsOverviewList = Sentry.withProfiler(observer(NewsOverviewListBase));
